import { effect, inject, Injectable } from "@angular/core";
import { Subject } from "rxjs";
import { Address } from "../../../shared/contract/Address/Address";
import { AVM } from "../../../shared/contract/AVM/AVM";
import { ObjectInformation } from "../../../shared/contract/ObjectInformation/ObjectInformation";
import { ValueFixing } from "../../../shared/contract/ValueFixing/ValueFixing";
import { RealEstateBaseObject } from "../models/real-estate-base-object.interface";
import { RealEstateStoreService } from "./real-estate-store.service";
import { hashValue } from "../../../shared/helpers/hash-helper";

export const REAL_ESTATE_EVENT_PROPERTIES: (keyof RealEstateBaseObject)[] = [
  "Address",
  "AVM",
  "ObjectInformation",
  "ValueFixing",
  "ValueFixingRent",
  "IsPreview"
];

export type RealEstateEventPropertyKey = (typeof REAL_ESTATE_EVENT_PROPERTIES)[number];

export type RealEstateSubjectMap = {
  [K in RealEstateEventPropertyKey]?: Subject<RealEstateBaseObject[K] | null>;
};

@Injectable({
  providedIn: "root"
})
export class RealEstateStoreEventsService {
  private readonly realEstateStoreService = inject(RealEstateStoreService);

  private readonly previousHashes = new Map<RealEstateEventPropertyKey, string>();

  private readonly addressChangeSubject = new Subject<Address | null>();
  private readonly avmChangeSubject = new Subject<AVM | null>();
  private readonly objectInformationChangeSubject = new Subject<ObjectInformation | null>();
  private readonly valueFixingChangeSubject = new Subject<ValueFixing | null>();
  private readonly valueFixingRentChangeSubject = new Subject<ValueFixing | null>();
  private readonly isPreviewChangeSubject = new Subject<boolean | null>();

  readonly addressChange$ = this.addressChangeSubject.asObservable();
  readonly avmChange$ = this.avmChangeSubject.asObservable();
  readonly objectInformationChange$ = this.objectInformationChangeSubject.asObservable();
  readonly valueFixingChange$ = this.valueFixingChangeSubject.asObservable();
  readonly valueFixingRentChange$ = this.valueFixingRentChangeSubject.asObservable();
  readonly isPreviewChange$ = this.isPreviewChangeSubject.asObservable();

  private readonly subjects: RealEstateSubjectMap = {
    Address: this.addressChangeSubject,
    AVM: this.avmChangeSubject,
    ObjectInformation: this.objectInformationChangeSubject,
    ValueFixing: this.valueFixingChangeSubject,
    ValueFixingRent: this.valueFixingRentChangeSubject,
    IsPreview: this.isPreviewChangeSubject
  };

  constructor() {
    effect(() => {
      const realEstate = this.realEstateStoreService.realEstateSignal();

      if (!realEstate) {
        for (const key of REAL_ESTATE_EVENT_PROPERTIES) {
          const subject = this.subjects[key];
          if (subject) {
            (subject as Subject<RealEstateBaseObject[RealEstateEventPropertyKey] | null>).next(null);
          }
        }
        this.previousHashes.clear();
        return;
      }

      for (const key of REAL_ESTATE_EVENT_PROPERTIES) {
        const value = realEstate[key];
        const currentHash = hashValue(value);
        const previousHash = this.previousHashes.get(key);

        if (previousHash !== currentHash) {
          this.previousHashes.set(key, currentHash);
          const subject = this.subjects[key];
          if (subject) {
            (subject as Subject<RealEstateBaseObject[RealEstateEventPropertyKey] | null>).next(value ?? null);
          }
        }
      }
    });
  }
}
